/**
 * Router configuration — maps between View enum and URL paths.
 * Single source of truth for all route definitions.
 */
import { View } from '../types';

/** Map each View to its canonical URL path */
export const viewToPath: Record<View, string> = {
  [View.LANDING]: '/',
  [View.LUMI]: '/lumi',
  [View.DEMO]: '/demo',
  [View.PRICING]: '/pricing',
  [View.GENERATOR]: '/generator',
  [View.EXTENSIONS]: '/extensions',
  [View.DOCUMENTATION]: '/docs',
  [View.AUTH]: '/auth',
  [View.DASHBOARD]: '/dashboard',
  [View.PRIVACY]: '/privacy',
  [View.TERMS]: '/terms',
  [View.LEGAL]: '/legal',
  [View.CONTACT]: '/contact',
  [View.COMMANDS]: '/commands',
  [View.FEATURES]: '/features',
  [View.HOW_IT_WORKS]: '/how-it-works',
  [View.HIGGSFIELD]: '/scrudix-x-higgsfield',
  [View.WORKCLOCK]: '/workclock',
  [View.TEAM_DASHBOARD]: '/team-dashboard',
  [View.STUDIO]: '/studio',
  [View.COMMUNITY]: '/community',
  [View.NOT_FOUND]: '/404',
  [View.BLOG]: '/blog',
  [View.BLOG_POST]: '/blog/:slug',
  [View.COMPARISON]: '/:competitor-vs-scrudix',
  [View.LEETCODE]: '/leetcode',
};

/** Reverse map: path → View */
export const pathToView: Record<string, View> = Object.fromEntries(
  Object.entries(viewToPath).map(([view, path]) => [path, view as View])
) as Record<string, View>;

/** Resolve the current pathname to a View (defaults to NOT_FOUND for unknown paths) */
export function resolveView(pathname: string): View {
  if (pathToView[pathname]) return pathToView[pathname];
  if (pathname.startsWith('/blog/') && pathname.length > '/blog/'.length) {
    return View.BLOG_POST;
  }
  if (/-vs-scrudix$/.test(pathname) && pathname.length > '-vs-scrudix'.length + 1) {
    return View.COMPARISON;
  }
  return View.NOT_FOUND;
}

/** Extract blog post slug from pathname */
export function getBlogSlug(pathname: string): string | null {
  if (!pathname.startsWith('/blog/')) return null;
  const slug = pathname.slice('/blog/'.length).split('/')[0];
  return slug || null;
}

/** Extract comparison page slug from pathname (e.g. /cursor-vs-scrudix) */
export function getComparisonSlug(pathname: string): string | null {
  const match = pathname.match(/^\/(.+-vs-scrudix)$/);
  if (!match) return null;
  return match[1] || null;
}
